import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, Lock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { onboardingLogin } from "@/lib/onboarding-api";
import { signInAsMember, signOutMember } from "@/lib/mock-data";
import { toast } from "sonner";

// PUB-04 — Login (rendered inside StandaloneShell)
export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("jennifer.walsh@gmail.com");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    // Admin accounts must use the dedicated admin sign-in URL (FR-AUTH-004)
    if (email.endsWith("@gcbc.com") || email.endsWith("@giftcardbuyersclub.com")) {
      setError("This is an admin account. Admin sign-in is on a separate URL — your admin should have shared the link directly.");
      return;
    }
    // Try the onboarding backend for anyone who isn't using the Jennifer demo persona,
    // OR whose password looks like a provisional credential (GCBC-XXXXXX).
    const looksProvisional = password.startsWith("GCBC-");
    const isDemoPersona = email === "jennifer.walsh@gmail.com" && !looksProvisional;
    if (!isDemoPersona) {
      setSubmitting(true);
      try {
        const result = await onboardingLogin(email, password);
        // Real user: hydrate their session FIRST so navigation paths see them.
        signInAsMember(result);
        // Persist the credentials hint so the password-change page knows the user.
        if (result.requiresPasswordChange) {
          sessionStorage.setItem("gcbc:tempCred", JSON.stringify({ email, password }));
          navigate("/auth/set-password");
          return;
        }
        toast.success(`Welcome back, ${result.firstName}`);
        navigate("/member/dashboard");
        return;
      } catch (err) {
        setError(err.message || "Sign-in failed. Please check your credentials.");
        setSubmitting(false);
        return;
      }
    }
    // Demo persona: ensure DEMO_MEMBER is pinned back to Jennifer (in case a real
    // user previously signed in), then drop into her seeded dashboard.
    signOutMember();
    navigate("/member/dashboard");
  };

  return (
    <div className="space-y-7">
      <div>
        <div className="eyebrow mb-3 text-brand-700">Member sign-in</div>
        <h1 className="font-brand text-[36px] leading-[1.1] tracking-[-0.02em] text-ink font-bold">Welcome back.</h1>
        <p className="mt-3 text-body text-ink-muted leading-relaxed">
          Sign in to view your reservations, submit cards, and track payouts.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
        <Field label="Email">
          <Input
            id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" autoFocus required data-testid="login-email"
            className="h-12 text-body bg-surface border-line"
          />
        </Field>
        <Field label="Password" right={<Link to="/auth/reset-password" className="text-caption font-semibold text-brand-700 hover:text-brand-800">Forgot password?</Link>}>
          <div className="relative">
            <Input
              id="password" type={showPwd ? "text" : "password"} value={password}
              onChange={(e) => setPassword(e.target.value)} required
              data-testid="login-password" className="h-12 text-body bg-surface border-line pr-11"
            />
            <button type="button" onClick={() => setShowPwd((s) => !s)} aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 size-8 grid place-items-center text-ink-hint hover:text-ink rounded-md hover:bg-surface-alt transition-colors">
              {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </Field>

        <label className="flex items-center gap-2.5 text-body-sm text-ink-muted select-none">
          <Checkbox checked={remember} onCheckedChange={(v) => setRemember(!!v)} /> Remember this device for 30 days
        </label>

        {error && (
          <div className="flex items-start gap-2 px-3.5 py-2.5 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
            <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{error}
          </div>
        )}

        <Button type="submit" disabled={submitting} className="w-full h-12 bg-brand-700 hover:bg-brand-800 text-body-sm font-semibold text-white group" data-testid="login-submit-btn">
          {submitting ? (
            <><Loader2 className="size-4 mr-2 animate-spin" /> Signing in…</>
          ) : (
            <>Sign in <span className="ml-2 transition-transform group-hover:translate-x-0.5">&rarr;</span></>
          )}
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-line" /></div>
        <div className="relative flex justify-center"><span className="px-3 bg-surface text-caption text-ink-hint uppercase tracking-wider">or</span></div>
      </div>

      <button type="button"
              className="w-full h-12 border border-line bg-surface hover:bg-surface-alt rounded-md text-body-sm font-semibold text-ink flex items-center justify-center gap-3 transition-colors">
        <GoogleG /> Continue with Google
      </button>

      <div className="text-center text-body-sm text-ink-muted">
        New here? <Link to="/apply" className="text-brand-700 font-semibold hover:text-brand-800">Apply to join</Link>
      </div>

      <div className="border-t border-line pt-5 flex items-start gap-2 text-caption text-ink-hint leading-relaxed">
        <Lock className="size-3.5 mt-0.5 shrink-0" />
        Encrypted in transit · 8 failed attempts then 30-minute lockout.
      </div>
    </div>
  );
}

function Field({ label, right, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-eyebrow uppercase text-ink-muted">{label}</Label>
        {right}
      </div>
      {children}
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.95l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
